import React, { Fragment, useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Heading from '@atlaskit/heading';
import type { IconButtonProps } from '@atlaskit/button/new';
import { IconButton } from '@atlaskit/button/new';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';

import { useContentState } from '@confluence/content-state/entry-points/useContentState.ts';
import { useElementSeenCallback } from '@confluence/analytics/entry-points/useElementSeenCallback.ts';
import { ContentAppearanceType } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { expValEquals } from '@confluence/feature-experiments';
import type { SummaryAnalyticsPropertiesType } from '@confluence/quick-summary/entry-points/summarySharedTypes.ts';

import { EndOfPageRecFeedbackForm } from './EndOfPageRecFeedbackForm.tsx';
import { EndOfPageRecGrid } from './EndOfPageRecGrid.tsx';
import { EndOfPageRecommendationCompactHeader } from './EndOfPageRecommendationCompactHeader.tsx';
import type { EndOfPageRecommendationQuery as EndOfPageRecommendationData } from './__types__/EndOfPageRecommendationQuery.ts';
import { END_OF_PAGE_RECOMMENDATIONS_METRIC } from './perf.config.ts';
import { i18n } from './messages.ts';
import { EndOfPageRecHeader, HeaderFrontContainer, HeaderLineContainer } from './SharedComponents.tsx';
import { EndOfPageRecommendationInfoPopup } from './EndOfPageRecommendationInfoPopup.tsx';
import { EndOfPageRecContainer } from './EndOfPageRecContainer.tsx';

type EndOfPageRecommendationComponentProps = {
	entityId: string;
	entityType: string;
	pageCardData: GlobalPageCard[] | undefined;
	data: EndOfPageRecommendationData | undefined;
	pageWidthType: string;
	spaceKey: string;
	isLivePage?: boolean;
};

const GRID_CARD_LIMIT = 6;
const GRID_CARD_LIMIT_LIVE_PAGES = 4;

export const EndOfPageRecommendationComponent = ({
	entityId,
	entityType,
	pageCardData,
	data,
	pageWidthType,
	isLivePage,
	spaceKey,
}: EndOfPageRecommendationComponentProps) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isInfoOpen, setInfoOpen] = useState(false);
	const [showFeedbackForm, setFeedbackForm] = useState(false);
	const [isCollapsed, setIsCollapsed] = useState(false);
	const compRef = useRef<HTMLDivElement | null>(null);
	const infoButtonRef = useRef<HTMLElement | null>(null);

	// eslint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook
	const [, actions] = useContentState();

	const gridCardLimit = isLivePage ? GRID_CARD_LIMIT_LIVE_PAGES : GRID_CARD_LIMIT;

	const pageRecIds = useMemo(
		() => pageCardData?.slice(0, gridCardLimit).map((content) => content.id),
		[pageCardData, gridCardLimit],
	);

	const contentRecsStrategies = useMemo(() => {
		const recommendations =
			data?.getRecommendedPages.recommendedPages.slice(0, gridCardLimit) || [];
		const strategiesArray = Array(gridCardLimit).fill('NULL');

		recommendations.forEach((rec, index) => {
			// Convert strategy arrays to simplified format for analytics
			// BEHAVIORAL -> B, CONTENT_TO_CONTENT -> C, [CONTENT_TO_CONTENT, BEHAVIORAL] -> BC, etc...
			strategiesArray[index] = rec.strategy
				.map((s) => s.charAt(0))
				.sort()
				.join('');
		});

		return strategiesArray;
	}, [data, gridCardLimit]);

	const spaceId = data?.space?.id;

	const tooltipOptions: IconButtonProps['tooltip'] = {
		position: 'right',
		hideTooltipOnClick: true,
	};

	const toggleInfo = useCallback(() => {
		if (!isInfoOpen) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: entityType,
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'endOfPageRecommendationsInfoButton',
					attributes: {
						contentId: entityId,
					},
				},
			}).fire();
		}

		setInfoOpen(!isInfoOpen);
	}, [createAnalyticsEvent, isInfoOpen, entityId, entityType]);

	const clickFeedback = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: entityType,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'endOfPageRecommendationsFeedback',
				attributes: {
					contentId: entityId,
				},
			},
		}).fire();

		setInfoOpen(false);
		setFeedbackForm(!showFeedbackForm);
	}, [createAnalyticsEvent, showFeedbackForm, entityId, entityType]);

	const closeSurvey = useCallback(() => {
		setFeedbackForm(false);
	}, [setFeedbackForm]);

	const onObserved = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: entityType,
				action: 'observed',
				actionSubject: 'endOfPageRecommendations',
				attributes: {
					strategies: contentRecsStrategies,
				},
				objectId: entityId,
				containerId: spaceId,
			},
		}).fire();
	}, [createAnalyticsEvent, entityId, contentRecsStrategies, spaceId, entityType]);

	const onCollapseExpandClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: entityType,
				action: isCollapsed ? 'expanded' : 'collapsed',
				actionSubject: 'endOfPageRecommendations',
				attributes: {
					strategies: contentRecsStrategies,
				},
				objectId: entityId,
				containerId: spaceId,
			},
		}).fire();

		setIsCollapsed(!isCollapsed);
	}, [createAnalyticsEvent, entityType, isCollapsed, contentRecsStrategies, entityId, spaceId]);

	useElementSeenCallback(compRef, onObserved);

	useEffect(() => {
		if (!!pageRecIds?.length && pageRecIds?.length > 1) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: entityType,
					action: 'rendered',
					actionSubject: 'endOfPageRecommendations',
					attributes: {
						strategies: contentRecsStrategies,
					},
					objectId: entityId,
					containerId: spaceId,
				},
			}).fire();
		}
	}, [createAnalyticsEvent, entityId, contentRecsStrategies, pageRecIds, spaceId, entityType]);

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSRed = process.env.REACT_SSR || window.__SSR_RENDERED__;

	const handleInfoButtonRef = (triggerProps) => {
		return (node) => {
			infoButtonRef.current = node;
			triggerProps.ref(node);
		};
	};

	const isDefaultWidth = pageWidthType
		? JSON.parse(pageWidthType) === ContentAppearanceType.DEFAULT
		: false;

	const analyticsProperties: SummaryAnalyticsPropertiesType = useMemo(
		() => ({
			source: 'endOfPageRecommendation',
			additionalAnalyticsAttributes: {
				contentType: entityType,
				strategies: contentRecsStrategies,
				spaceKey,
				spaceId,
				recommendationCount: pageCardData?.length || 0,
			},
		}),
		[entityType, contentRecsStrategies, spaceKey, spaceId, pageCardData?.length],
	);

	return !!pageCardData?.length ? (
		<Fragment>
			{!isSSRed && (actions?.addContentToBeLoaded('endOfPageRecommendation', 'loading'), null)}
			<EndOfPageRecContainer
				isDefaultWidth={isDefaultWidth}
				ssrPlaceholderIdProp={ssrPlaceholderIdProp}
			>
				{expValEquals('cc_ai_related_pages_compact', 'cohort', 'test') ? (
					<EndOfPageRecommendationCompactHeader
						sendFeedback={clickFeedback}
						toggleInfo={toggleInfo}
						isInfoOpen={isInfoOpen}
						handleInfoButtonRef={handleInfoButtonRef}
						analyticsProperties={analyticsProperties}
						contentId={entityId}
					/>
				) : (
					<EndOfPageRecHeader>
						<HeaderFrontContainer>
							<Heading size="xsmall" as="h2">
								<FormattedMessage {...i18n.title} />
							</Heading>
							<EndOfPageRecommendationInfoPopup
								toggleInfo={toggleInfo}
								clickFeedback={clickFeedback}
								isInfoOpen={isInfoOpen}
								handleInfoButtonRef={handleInfoButtonRef}
								showFeedbackButton
							/>
						</HeaderFrontContainer>
						<HeaderLineContainer />
						<IconButton
							appearance="subtle"
							icon={(iconProps) =>
								isCollapsed ? (
									<ChevronDownIcon {...iconProps} size="small" />
								) : (
									<ChevronUpIcon {...iconProps} size="small" />
								)
							}
							label={
								isCollapsed ? (
									<FormattedMessage {...i18n.expandButton} />
								) : (
									<FormattedMessage {...i18n.collapseButton} />
								)
							}
							onClick={onCollapseExpandClick}
							isTooltipDisabled={false}
							tooltip={tooltipOptions}
							testId={isCollapsed ? 'eop-expand-button' : 'eop-collapse-button'}
						/>
					</EndOfPageRecHeader>
				)}

				<ExitingPersistence appear={false}>
					{!isCollapsed && (
						<FadeIn entranceDirection="top" exitDirection="top" duration="medium">
							{(props) => (
								<div {...props}>
									<EndOfPageRecGrid
										isCompact={expValEquals('cc_ai_related_pages_compact', 'cohort', 'test')}
										pageCardData={pageCardData.slice(0, gridCardLimit)}
										pageRecStrategy={contentRecsStrategies}
										entityId={entityId}
									/>
									<div ref={compRef} />
								</div>
							)}
						</FadeIn>
					)}
				</ExitingPersistence>
			</EndOfPageRecContainer>
			{showFeedbackForm && (
				<EndOfPageRecFeedbackForm
					closeSurvey={closeSurvey}
					entityId={entityId}
					recIds={pageRecIds}
					sources={contentRecsStrategies}
					shouldReturnFocus={infoButtonRef}
				/>
			)}
			<PageSegmentLoadEnd key={entityId} metric={END_OF_PAGE_RECOMMENDATIONS_METRIC} />
			{!isSSRed && (actions?.removeContentFromLoading('endOfPageRecommendation'), null)}
		</Fragment>
	) : null;
};
